import { create } from "zustand";

export type stepType =
  | "JD"
  | "PER_INFO"
  | "WORK_EXP"
  | "EDU"
  | "SKILLS"
  | "AWARDS"
  | "GENERATE"
  | "CERTI"
  | "LANGUAGES"
  | "HOBBIES"
  | "TEMPLATES";
export type jobAnalysisType = {
  requiredSkills: string[];
  responsibilities: string[];
  experience: string;
  parsedSkills: string[];
};
export type personalInfoType = {
  name: string;
  phone: string;
  email: string;
  linkedin: string;
};
export type workExpInfoType = {
  jobCompanyName: string;
  jobtitleAndResp: string;
  jobDuration: string;
  aiSugg: string[];
  jobUniqueImpact: string;
};
export type educationInfoType = {
  degree: string;
  duration: string;
  institute: string;
  major: string;
};
export type AudioChatState = {
  //--- chat loader
  isLoading: boolean;
  setIsLoading: (res: boolean) => void;
  //--- steps
  step: stepType;
  setStep: (res: stepType) => void;
  progressBar: number;
  progressBarDesc: string;
  //--- chat toolbar
  chatTextData?: string;
  setChatTextData: (res: string) => void;
  //--- data collected
  jobDescription: string;
  jobAnalysis: jobAnalysisType;
  personalInfo: personalInfoType;
  workExpInfo: workExpInfoType[];
  selectedSkills: string[];
  educationInfo: educationInfoType[];
  awardsInfo: string[];
  certificationInfo: string[];
  languagesInfo: string[];
  hobbiesInfo: string[];
  userSummary: string;
  currentTemplate: string;
  updateJobDescription: (res: string) => void;
  updateJobAnalysis: (res: jobAnalysisType) => void;
  updatePersonalInfo: (type: keyof personalInfoType, res: string) => void;
  updateWorkExpInfoCompany: (index: number, res: string) => void;
  updateWorkExpInfoTitle: (index: number, res: string) => void;
  updateWorkExpInfoDuration: (index: number, res: string) => void;
  updateWorkExpInfoAiSugg: (index: number, res: string[]) => void;
  updateWorkExpInfojobExp: (index: number, res: string) => void;
  updateWorkExpInfoAddNewJob: () => void;
  updateSelectedSkills: (res: string) => void;
  updateEducationInfoDegree: (index: number, res: string) => void;
  updateEducationInfoDuration: (index: number, res: string) => void;
  updateEducationInfoInstitute: (index: number, res: string) => void;
  updateEducationInfoMajor: (index: number, res: string) => void;
  updateEducationInfAddNewEdu: () => void;
  updateAwardsInfo: (res: string) => void;
  updateCertificationInfo: (res: string) => void;
  updateLanguagesInfo: (res: string) => void;
  updateHobbiesInfo: (res: string) => void;
  updateUserSummary: (res: string) => void;
  updateCurrentTemplate: (res: string) => void;
  //--- reset
  resetAudioChat: () => void;
};

export const useAudioChatStore = create<AudioChatState>()((set) => ({
  //--- chat loader
  isLoading: false,
  setIsLoading: (res) =>
    set({
      isLoading: res,
    }),
  //--- steps
  step: "JD",
  setStep: (res) =>
    set(() => {
      let newProgressBar = 0;
      let newprogressBarDesc = "";
      switch (res) {
        case "JD": {
          newProgressBar = 0;
          newprogressBarDesc = "Add Job Description";
          break;
        }
        case "PER_INFO": {
          newProgressBar = 17;
          newprogressBarDesc = "Add Personal Details";
          break;
        }
        case "WORK_EXP": {
          newProgressBar = 34;
          newprogressBarDesc = "Add Work Experience";
          break;
        }
        case "SKILLS": {
          newProgressBar = 51;
          newprogressBarDesc = "Add Skills";
          break;
        }
        case "EDU": {
          newProgressBar = 68;
          newprogressBarDesc = "Add Education Details";
          break;
        }
        case "TEMPLATES": {
          newProgressBar = 84;
          newprogressBarDesc = "Select Template";
          break;
        }
        case "GENERATE": {
          newProgressBar = 100;
          newprogressBarDesc = "Generate Resume";
          break;
        }
        case "AWARDS": {
          newprogressBarDesc = "Add Awards";
          break;
        }
        case "CERTI": {
          newprogressBarDesc = "Add Certifications";
          break;
        }
        case "HOBBIES": {
          newprogressBarDesc = "Add Hobbies";
          break;
        }
        case "LANGUAGES": {
          newprogressBarDesc = "Add Languages";
          break;
        }
        default:
          break;
      }
      return {
        step: res,
        progressBar: newProgressBar,
        progressBarDesc: newprogressBarDesc,
      };
    }),
  progressBar: 0,
  progressBarDesc: "Add Job Description",
  //--- chat toolbar
  chatTextData: "",
  setChatTextData: (res) =>
    set({
      chatTextData: res,
    }),
  //--- data collected
  jobDescription: "",
  jobAnalysis: { requiredSkills: [], responsibilities: [], experience: "", parsedSkills: [] },
  personalInfo: {
    name: "",
    phone: "",
    email: "",
    linkedin: "",
  },
  workExpInfo: [
    {
      jobCompanyName: "",
      jobtitleAndResp: "",
      jobDuration: "",
      aiSugg: [],
      jobUniqueImpact: "",
    },
  ],
  selectedSkills: [],
  educationInfo: [
    {
      degree: "",
      duration: "",
      institute: "",
      major: "",
    },
  ],
  awardsInfo: [],
  certificationInfo: [],
  languagesInfo: [],
  hobbiesInfo: [],
  userSummary: "",
  currentTemplate: "naruto",
  updateJobDescription: (res) =>
    set({
      jobDescription: res,
    }),
  updateJobAnalysis: (res) =>
    set({
      jobAnalysis: res,
    }),
  updatePersonalInfo: (type, res) =>
    set((state) => {
      return {
        personalInfo: { ...state.personalInfo, [type]: res },
      };
    }),
  updateWorkExpInfoCompany: (index, res) =>
    set((state) => {
      const newWorkExpInfo = [...state.workExpInfo];
      newWorkExpInfo[index].jobCompanyName = res;
      return { workExpInfo: newWorkExpInfo };
    }),
  updateWorkExpInfoTitle: (index, res) =>
    set((state) => {
      const newWorkExpInfo = [...state.workExpInfo];
      newWorkExpInfo[index].jobtitleAndResp = res;
      return { workExpInfo: newWorkExpInfo };
    }),
  updateWorkExpInfoDuration: (index, res) =>
    set((state) => {
      const newWorkExpInfo = [...state.workExpInfo];
      newWorkExpInfo[index].jobDuration = res;
      return { workExpInfo: newWorkExpInfo };
    }),
  updateWorkExpInfoAiSugg: (index, res) =>
    set((state) => {
      const newWorkExpInfo = [...state.workExpInfo];
      newWorkExpInfo[index].aiSugg = res;
      return { workExpInfo: newWorkExpInfo };
    }),
  updateWorkExpInfojobExp: (index, res) =>
    set((state) => {
      const newWorkExpInfo = [...state.workExpInfo];
      newWorkExpInfo[index].jobUniqueImpact = res;
      return { workExpInfo: newWorkExpInfo };
    }),
  updateWorkExpInfoAddNewJob: () =>
    set((state) => {
      return {
        workExpInfo: [
          ...state.workExpInfo,
          {
            jobCompanyName: "",
            jobtitleAndResp: "",
            jobDuration: "",
            aiSugg: [],
            jobUniqueImpact: "",
          },
        ],
      };
    }),
  updateSelectedSkills: (res) =>
    set((state) => {
      const newSkills = [...state.selectedSkills];
      if (newSkills.includes(res)) {
        const filteredSkills = newSkills.filter((s) => s !== res);
        return {
          selectedSkills: filteredSkills,
        };
      } else {
        return {
          selectedSkills: [...newSkills, res],
        };
      }
    }),
  updateEducationInfoDegree: (index, res) =>
    set((state) => {
      const newEducationInfo = [...state.educationInfo];
      newEducationInfo[index].degree = res;
      return { educationInfo: newEducationInfo };
    }),
  updateEducationInfoDuration: (index, res) =>
    set((state) => {
      const newEducationInfo = [...state.educationInfo];
      newEducationInfo[index].duration = res;
      return { educationInfo: newEducationInfo };
    }),
  updateEducationInfoInstitute: (index, res) =>
    set((state) => {
      const newEducationInfo = [...state.educationInfo];
      newEducationInfo[index].institute = res;
      return { educationInfo: newEducationInfo };
    }),
  updateEducationInfoMajor: (index, res) =>
    set((state) => {
      const newEducationInfo = [...state.educationInfo];
      newEducationInfo[index].major = res;
      return { educationInfo: newEducationInfo };
    }),
  updateEducationInfAddNewEdu: () =>
    set((state) => {
      return {
        educationInfo: [
          ...state.educationInfo,
          {
            degree: "",
            duration: "",
            institute: "",
            major: "",
          },
        ],
      };
    }),
  updateAwardsInfo: (res) =>
    set((state) => {
      const newAwards = [...state.awardsInfo];
      if (newAwards.includes(res)) {
        const filteredAwards = newAwards.filter((s) => s !== res);
        return {
          awardsInfo: filteredAwards,
        };
      } else {
        return {
          awardsInfo: [...newAwards, res],
        };
      }
    }),
  updateCertificationInfo: (res) =>
    set((state) => {
      const newCertification = [...state.certificationInfo];
      if (newCertification.includes(res)) {
        const filteredCertification = newCertification.filter((s) => s !== res);
        return {
          certificationInfo: filteredCertification,
        };
      } else {
        return {
          certificationInfo: [...newCertification, res],
        };
      }
    }),
  updateLanguagesInfo: (res) =>
    set((state) => {
      const newLanguages = [...state.languagesInfo];
      if (newLanguages.includes(res)) {
        const filteredLanguages = newLanguages.filter((s) => s !== res);
        return {
          languagesInfo: filteredLanguages,
        };
      } else {
        return {
          languagesInfo: [...newLanguages, res],
        };
      }
    }),
  updateHobbiesInfo: (res) =>
    set((state) => {
      const newHobbies = [...state.hobbiesInfo];
      if (newHobbies.includes(res)) {
        const filteredHobbies = newHobbies.filter((s) => s !== res);
        return {
          hobbiesInfo: filteredHobbies,
        };
      } else {
        return {
          hobbiesInfo: [...newHobbies, res],
        };
      }
    }),
  updateUserSummary: (res) =>
    set({
      userSummary: res,
    }),
  updateCurrentTemplate: (res) =>
    set({
      currentTemplate: res,
    }),
  //--- reset
  resetAudioChat: () =>
    set({
      step: "JD",
      isLoading: false,
      progressBar: 0,
      progressBarDesc: "Add Job Description",
      chatTextData: "",
      jobDescription: "",
      jobAnalysis: { requiredSkills: [], responsibilities: [], experience: "", parsedSkills: [] },
      personalInfo: {
        name: "",
        phone: "",
        email: "",
        linkedin: "",
      },
      workExpInfo: [
        {
          jobCompanyName: "",
          jobtitleAndResp: "",
          jobDuration: "",
          aiSugg: [],
          jobUniqueImpact: "",
        },
      ],
      selectedSkills: [],
      educationInfo: [
        {
          degree: "",
          duration: "",
          institute: "",
          major: "",
        },
      ],
      awardsInfo: [],
      certificationInfo: [],
      languagesInfo: [],
      hobbiesInfo: [],
      userSummary: "",
      currentTemplate: "naruto",
    }),
}));
